import { useAuthStore } from '@/stores/auth.store';
import { useHasPermission } from '@/hooks/usePermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api/client';
import { operationsPricebookEndpoints, supplierEndpoints } from '@/lib/api/endpoints';
import type {
  ItemSupplier,
  Supplier,
  SupplierBranch,
  SupplierContact,
  SupplierCostHistoryEntry,
} from '@/types/pricebook';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';

/** Ordering data on a supplier catalog row (PUR-CAT-5). */
export interface ItemSupplierOrdering {
  minimumOrderQuantity?: number | null;
  orderMultiple?: number | null;
  leadTimeDays?: number | null;
  supplierItemDescription?: string | null;
  isDiscontinued?: boolean;
}

const keys = {
  suppliers: ['pricebook', 'suppliers'] as const,
  itemSuppliers: (itemId: string) => ['pricebook', 'item-suppliers', itemId] as const,
};

function useSupplierAuthKey() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const userId = useAuthStore((s) => s.userId);
  const version = useAuthStore((s) => s.permissionsVersion);
  return [tenantId, userId, version];
}

/** The companies we buy from. Inactive ones are excluded by default. */
export function useSuppliers(includeInactive = false) {
  const authKey = useSupplierAuthKey();
  const canView = useHasPermission('suppliers:view');
  return useQuery({
    queryKey: [...keys.suppliers, includeInactive, ...authKey],
    enabled: canView,
    queryFn: () =>
      apiGet<Supplier[]>(
        operationsClient,
        `${supplierEndpoints.list()}${includeInactive ? '?includeInactive=true' : ''}`,
      ),
  });
}

export function useSaveSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    // Only the fields the API accepts: editing starts from a fetched row and
    // the API rejects unknown properties.
    mutationFn: ({ id, ...body }: Partial<Supplier> & { name: string }) => {
      // Profile fields go only when the caller set them, so the quick editor
      // (name and contact details) never clears terms it does not show.
      const profile = Object.fromEntries(
        SUPPLIER_PROFILE_FIELDS.filter((key) => body[key] !== undefined).map((key) => [
          key,
          body[key],
        ]),
      );
      const payload = {
        name: body.name,
        isActive: body.isActive,
        accountNumber: body.accountNumber ?? null,
        email: body.email ?? null,
        phone: body.phone ?? null,
        website: body.website ?? null,
        notes: body.notes ?? null,
        ...profile,
      };
      return id
        ? apiPatch<Supplier>(operationsClient, supplierEndpoints.update(id), payload)
        : apiPost<Supplier>(operationsClient, supplierEndpoints.create(), payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.suppliers });
    },
  });
}

/** Profile fields the supplier API accepts beyond name and contact details. */
const SUPPLIER_PROFILE_FIELDS = [
  'code',
  'legalName',
  'onHold',
  'holdReason',
  'orderEmail',
  'apEmail',
  'remitToAddress',
  'orderingAddress',
  'currency',
  'paymentTermsDays',
  'paymentTermsLabel',
  'earlyPayDiscountPercent',
  'earlyPayDiscountDays',
  'defaultLeadTimeDays',
  'minimumOrderAmount',
  'freeFreightThreshold',
  'freightTerms',
  'defaultTaxMode',
  'allowFieldPurchases',
  'businessUnitIds',
  'costUpdatePolicy',
  'vendor1099',
] as const satisfies ReadonlyArray<keyof Supplier>;

export function useSupplier(id: string | undefined) {
  const authKey = useSupplierAuthKey();
  const canView = useHasPermission('suppliers:view');
  return useQuery({
    queryKey: [...keys.suppliers, 'detail', id, ...authKey],
    enabled: canView && !!id,
    queryFn: () => apiGet<Supplier>(operationsClient, supplierEndpoints.detail(id!)),
  });
}

export function useSupplierContacts(supplierId: string) {
  const authKey = useSupplierAuthKey();
  return useQuery({
    queryKey: [...keys.suppliers, 'contacts', supplierId, ...authKey],
    queryFn: () =>
      apiGet<SupplierContact[]>(operationsClient, supplierEndpoints.contacts(supplierId)),
  });
}

export function useSupplierBranches(supplierId: string) {
  const authKey = useSupplierAuthKey();
  return useQuery({
    queryKey: [...keys.suppliers, 'branches', supplierId, ...authKey],
    queryFn: () =>
      apiGet<SupplierBranch[]>(
        operationsClient,
        `${supplierEndpoints.branches(supplierId)}?includeInactive=true`,
      ),
  });
}

type ContactInput = Omit<SupplierContact, 'id' | 'supplierId'> & { id?: string };
// Coordinates are optional: a branch saved without them keeps what it has.
type BranchInput = Omit<SupplierBranch, 'id' | 'supplierId' | 'latitude' | 'longitude'> & {
  id?: string;
  latitude?: number | null;
  longitude?: number | null;
};

export function useSupplierContactMutations(supplierId: string) {
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: [...keys.suppliers, 'contacts', supplierId] });
  return {
    save: useMutation({
      mutationFn: ({ id, ...body }: ContactInput) =>
        id
          ? apiPatch<SupplierContact>(
              operationsClient,
              supplierEndpoints.contact(supplierId, id),
              body,
            )
          : apiPost<SupplierContact>(
              operationsClient,
              supplierEndpoints.contacts(supplierId),
              body,
            ),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (contactId: string) =>
        apiDelete(operationsClient, supplierEndpoints.contact(supplierId, contactId)),
      onSuccess: refresh,
    }),
  };
}

export function useSaveSupplierBranch(supplierId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: BranchInput) =>
      id
        ? apiPatch<SupplierBranch>(operationsClient, supplierEndpoints.branch(supplierId, id), body)
        : apiPost<SupplierBranch>(operationsClient, supplierEndpoints.branches(supplierId), body),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: [...keys.suppliers, 'branches', supplierId],
      }),
  });
}

/**
 * Attach a supplier to an item whose id is only known at call time.
 *
 * The create form collects suppliers before the item exists, so the id cannot
 * be bound when the hook is set up the way `useItemSupplierMutations` does.
 */
export function useAttachSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      ...body
    }: {
      itemId: string;
      supplierId: string;
      cost: number;
      purchaseUnit?: string;
      unitsPerPurchaseUnit?: number;
      supplierPartNumber?: string | null;
      isPrimary?: boolean;
    }) =>
      apiPost<ItemSupplier>(operationsClient, operationsPricebookEndpoints.suppliers(itemId), body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: keys.itemSuppliers(variables.itemId),
      });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'items'] });
    },
  });
}

/** Who supplies one catalog item, primary first. */
export function useItemSuppliers(itemId?: string) {
  const authKey = useSupplierAuthKey();
  const canView = useHasPermission('suppliers:view');
  return useQuery({
    queryKey: [...keys.itemSuppliers(itemId ?? ''), ...authKey],
    queryFn: () =>
      apiGet<ItemSupplier[]>(operationsClient, operationsPricebookEndpoints.suppliers(itemId!)),
    enabled: Boolean(itemId) && canView,
  });
}

/**
 * Every write against an item's suppliers.
 *
 * They all invalidate the ITEM too, not just the supplier list: promoting a
 * supplier or changing the primary's cost rewrites the item's own cost, so a
 * stale item would show a cost its supplier no longer charges.
 */
export function useItemSupplierMutations(itemId?: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: keys.itemSuppliers(itemId ?? ''),
    });
    void queryClient.invalidateQueries({
      queryKey: ['pricebook', 'item', itemId],
    });
    void queryClient.invalidateQueries({ queryKey: ['pricebook', 'items'] });
  };

  const attach = useMutation({
    mutationFn: (body: {
      supplierId: string;
      cost: number;
      purchaseUnit?: string;
      unitsPerPurchaseUnit?: number;
      supplierPartNumber?: string | null;
      isPrimary?: boolean;
    }) =>
      apiPost<ItemSupplier>(
        operationsClient,
        operationsPricebookEndpoints.suppliers(itemId!),
        body,
      ),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({
      itemSupplierId,
      ...body
    }: {
      itemSupplierId: string;
      cost?: number;
      purchaseUnit?: string;
      unitsPerPurchaseUnit?: number;
      supplierPartNumber?: string | null;
    } & ItemSupplierOrdering) =>
      apiPatch<ItemSupplier>(
        operationsClient,
        operationsPricebookEndpoints.itemSupplier(itemId!, itemSupplierId),
        body,
      ),
    onSuccess: () => {
      refresh();
      // A cost edit is a new history entry.
      void queryClient.invalidateQueries({
        queryKey: ['pricebook', 'item-cost-history', itemId ?? ''],
      });
    },
  });

  const setPrimary = useMutation({
    mutationFn: (itemSupplierId: string) =>
      apiPost<ItemSupplier>(
        operationsClient,
        operationsPricebookEndpoints.itemSupplierPrimary(itemId!, itemSupplierId),
        {},
      ),
    onSuccess: refresh,
  });

  const detach = useMutation({
    mutationFn: ({
      itemSupplierId,
      replacementItemSupplierId,
    }: {
      itemSupplierId: string;
      replacementItemSupplierId?: string;
    }) =>
      apiDelete<{ id: string }>(
        operationsClient,
        operationsPricebookEndpoints.itemSupplier(itemId!, itemSupplierId),
        // Axios sends a DELETE body only under `data`.
        { data: { replacementItemSupplierId } },
      ),
    onSuccess: refresh,
  });

  return { attach, update, setPrimary, detach };
}

/** Every cost set or seen for an item, per supplier, newest first (PUR-CAT-6). */
export function useItemCostHistory(itemId?: string) {
  const authKey = useSupplierAuthKey();
  const canView = useHasPermission('suppliers:view');
  return useQuery({
    queryKey: ['pricebook', 'item-cost-history', itemId ?? '', ...authKey],
    queryFn: () =>
      apiGet<SupplierCostHistoryEntry[]>(
        operationsClient,
        operationsPricebookEndpoints.itemCostHistory(itemId!),
      ),
    enabled: Boolean(itemId) && canView,
  });
}

export interface ItemReplacement {
  replacementItem: { id: string; name: string; code: string; unit: string } | null;
}

/** What to order once suppliers stop selling an item (PUR-CAT-12). */
export function useItemReplacement(itemId?: string) {
  const authKey = useSupplierAuthKey();
  const purchasingEnabled = usePurchasingEnabled();
  const canView = useHasPermission('suppliers:view') && purchasingEnabled;
  const queryClient = useQueryClient();
  const key = ['pricebook', 'item-replacement', itemId ?? ''];
  const query = useQuery({
    queryKey: [...key, ...authKey],
    queryFn: () =>
      apiGet<ItemReplacement>(
        operationsClient,
        operationsPricebookEndpoints.itemReplacement(itemId!),
      ),
    enabled: Boolean(itemId) && canView,
  });
  const save = useMutation({
    mutationFn: (replacementItemId: string | null) =>
      apiPut<ItemReplacement>(
        operationsClient,
        operationsPricebookEndpoints.itemReplacement(itemId!),
        { replacementItemId },
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  return { ...query, save };
}
